/* eslint-disable no-underscore-dangle */
/*
 * This router:
 * - Maintains the `currentPath` accessible by the `currentPath` function.
 * - Provides a `go` function for navigating to a new path.
 * - Accepts a `resolvePath` function (which can return a promise) for determining
 *   whether a path can be navigated to.
 * - Provides a `subscribe` function for running side effects when the
 *   current path changes.
 *
 * This router does not but should:
 * - Handle parameters on the path and pass then into subscribed callbacks.
 *
 * Usage:
 *
 * ```js
 * const resolvePath = store => targetPath => {
 *   switch (targetPath) {
 *     case '/protected':
 *       return store.isAuthorized() ? targetPath : '/';
 *     default:
 *       return '/';
 *   }
 * };
 *
 * const store = {
 *   isAuthorized: () => true,
 * };
 *
 * const router = createRouter(resolvePath(store));
 *
 * router.subscribe(({ newPath }) => {
 *   console.log(newPath);
 * });
 *
 * router.go(); // /
 * router.go('/protected'); // /protected
 * ```
 */

export function createRouter(resolvePath = (targetPath) => targetPath) {
  let _currentPath = null;
  let _callbacks = [];
  let destroyed;

  function setCurrentPath(targetPath) {
    _currentPath = targetPath;
  }

  return {
    currentPath() {
      return _currentPath;
    },
    setCurrentPathAndDontFireCallbacks: setCurrentPath,
    go(targetPath) {
      if (targetPath !== _currentPath) {
        return new Promise((res) => {
          Promise.resolve(resolvePath(targetPath)).then((newPath) => {
            const prevPath = _currentPath;
            setCurrentPath(newPath);

            if (!destroyed && prevPath !== newPath) {
              _callbacks.forEach((callback) =>
                callback({ newPath: _currentPath, prevPath })
              );
            }

            return res({ newPath: _currentPath, prevPath });
          });
        });
      }

      return Promise.resolve({ newPath: _currentPath, prevPath: _currentPath });
    },
    subscribe(fn) {
      _callbacks.push(fn);

      return () => {
        _callbacks = _callbacks.reduce(
          (acc, val) => (val === fn ? acc : [...acc, val]),
          []
        );
      };
    },
    destroy() {
      destroyed = true;
      _callbacks = [];
    },
  };
}
